import React from 'react';
import { motion } from 'framer-motion';
import { Star, Quote, Phone } from 'lucide-react';

const RATING = 4.9;
const REVIEW_COUNT = 340;

const TESTIMONIALS = [
  {
    quote: 'Gece 2\'de aradım, 25 dakikada geldiler. Hayat kurtarıcı servis.',
    name: 'Ahmet Y.',
    location: 'Gebze',
    time: '3 gün önce',
    stars: 5,
  },
  {
    quote: 'Otobanda lastiğim patladı. Panik yaptım ama 20 dakikada yanımdaydılar. Profesyonel ekip.',
    name: 'Murat T.',
    location: 'Darıca, TEM Otoyolu',
    time: '1 hafta önce',
    stars: 5,
  },
  {
    quote: 'Hiç uğraşmadan yerinde çözdüler. Fiyat da gayet makul. Kesinlikle tavsiye ederim.',
    name: 'Fatma K.',
    location: 'İzmit',
    time: '2 hafta önce',
    stars: 5,
  },
];

function Stars({ count }) {
  return (
    <div style={{ display: 'flex', gap: '.15rem' }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={`star-${i}`}
          size={14}
          fill={i < count ? '#facc15' : 'transparent'}
          stroke={i < count ? '#facc15' : 'var(--border2)'}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

export default function SocialProof() {
  return (
    <section
      data-testid="social-proof"
      className="section"
      style={{
        background: 'var(--bg)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Ambient */}
      <div style={{
        position: 'absolute', top: '30%', right: '10%',
        width: 500, height: 400,
        background: 'radial-gradient(ellipse, rgba(250,204,21,.04) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <div className="wrap" style={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div className="pill" style={{
            marginBottom: '1rem',
            background: 'rgba(250,204,21,.08)',
            border: '1px solid rgba(250,204,21,.2)',
            color: '#facc15',
          }}>
            Müşteri Deneyimleri
          </div>

          {/* Big rating */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '.75rem', marginBottom: '.75rem' }}>
            <span style={{
              fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800,
              fontSize: '2.5rem', color: 'var(--text)', lineHeight: 1,
              letterSpacing: '-.04em',
            }}>{RATING}</span>
            <div>
              <Stars count={5} />
              <span style={{ fontSize: '.72rem', color: 'var(--text2)', marginTop: '.2rem', display: 'block' }}>
                {REVIEW_COUNT}+ değerlendirme
              </span>
            </div>
          </div>

          <h2 className="h2">
            Bize Güvenenlerin <span style={{ color: '#facc15' }}>Sözü</span>
          </h2>
        </div>

        {/* Testimonials */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '1.25rem',
        }} className="proof-grid">
          {TESTIMONIALS.map((t, i) => (
            <motion.div
              key={`testimonial-${t.name}`}
              data-testid={`testimonial-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: .5, delay: i * .1 }}
              style={{
                background: 'var(--card)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--rl)',
                padding: '2rem',
                position: 'relative',
                transition: 'border-color .25s, transform .2s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'rgba(250,204,21,.25)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {/* Quote icon */}
              <div style={{
                position: 'absolute', top: '1.25rem', right: '1.25rem',
                color: 'var(--border2)', opacity: .4,
              }}>
                <Quote size={24} />
              </div>

              <Stars count={t.stars} />

              <p style={{
                fontFamily: "'Space Grotesk',sans-serif",
                fontSize: '.95rem', fontWeight: 500,
                color: 'var(--text)', lineHeight: 1.6,
                margin: '1rem 0 1.25rem',
                minHeight: 60,
              }}>
                "{t.quote}"
              </p>

              <div style={{ borderTop: '1px solid var(--border)', paddingTop: '.875rem' }}>
                <div style={{
                  fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700,
                  fontSize: '.88rem', color: 'var(--text)',
                }}>{t.name}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '.2rem' }}>
                  <span style={{ fontSize: '.75rem', color: 'var(--text2)' }}>{t.location}</span>
                  <span style={{ fontSize: '.68rem', color: 'var(--muted)' }}>{t.time}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Conversion nudge — neuromarketing: after social proof, reduce friction */}
        <div style={{
          marginTop: '2.5rem', textAlign: 'center',
        }}>
          <p style={{ fontSize: '.85rem', color: 'var(--text2)', marginBottom: '1rem' }}>
            Onlar güvendi. <strong style={{ color: 'var(--text)' }}>Sıra sizde.</strong>
          </p>
          <a
            href="tel:05349687611"
            data-testid="social-proof-cta"
            className="btn btn-primary num ring"
            style={{ gap: '.5rem' }}
          >
            <Phone size={15} /> Hemen Ara — Beklemeyin
          </a>
        </div>
      </div>

      <style>{`
        @media(max-width:768px){
          .proof-grid{grid-template-columns:1fr!important}
        }
      `}</style>
    </section>
  );
}
